/* ============================================================
   SOLENNE — tabs + slider (built on Dawn). Self-contained.
   <sln-tabs>: tablist with roving tabindex + arrow-key navigation.
   <sln-slider>: scroll-snap carousel (arrows, keyboard, reduced motion).
   ============================================================ */
(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  class SlnTabs extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.tabs = Array.from(this.querySelectorAll('[data-sln-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-sln-tab-panel]'));

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') { event.preventDefault(); this.focusTab((index + 1) % this.tabs.length); }
          else if (event.key === 'ArrowLeft') { event.preventDefault(); this.focusTab((index - 1 + this.tabs.length) % this.tabs.length); }
          else if (event.key === 'Home') { event.preventDefault(); this.focusTab(0); }
          else if (event.key === 'End') { event.preventDefault(); this.focusTab(this.tabs.length - 1); }
        });
      });
    }

    focusTab(index) {
      this.tabs[index].focus();
      this.select(index);
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.classList.toggle('is-active', i === index);
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
        panel.hidden = i !== index;
      });
    }
  }

  class SlnSlider extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.viewport = this.querySelector('[data-sln-slider-viewport]');
      this.track = this.querySelector('[data-sln-slider-track]');
      this.prev = this.querySelector('[data-sln-slider-prev]');
      this.next = this.querySelector('[data-sln-slider-next]');
      if (!this.viewport || !this.track) return;

      if (this.prev) this.prev.addEventListener('click', () => this.scrollBy(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollBy(1));

      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.scrollBy(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.scrollBy(-1); }
      });

      this.viewport.addEventListener('scroll', debounce(() => this.updateArrows(), 100), { passive: true });
      this.updateArrows();
    }

    step() {
      const card = this.track.querySelector('.sln-slider__card');
      if (!card) return this.viewport.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).gap || '0') || 0;
      return card.offsetWidth + gap;
    }

    scrollBy(direction) {
      this.viewport.scrollBy({ left: direction * this.step(), behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    updateArrows() {
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (this.prev) this.prev.disabled = this.viewport.scrollLeft <= 1;
      if (this.next) this.next.disabled = this.viewport.scrollLeft >= max - 1;
    }
  }

  customElements.define('sln-tabs', SlnTabs);
  customElements.define('sln-slider', SlnSlider);
})();
